/**
 * modalityPanel.js — В ПРАВОЙ ПАНЕЛИ ТОЛЬКО ТО, ЧТО РАБОТАЕТ НА ОТКРЫТОЙ СЕРИИ. [12.0.55]
 *
 * Правило — core/modalityPanelCore.js (под тестами). Здесь — страница.
 *
 * Владелец: «пользователь просматривает МРТ и не понимает, почему многие
 * кнопки и функции не работают. Нужно оставлять в каждом режиме только те
 * кнопки, которые ему предназначены». Режим — модальность открытой серии
 * (тег DICOM), не догадка по области тела.
 *
 * ЧТО ПРЯЧЕТСЯ — по разметке index.html, атрибут data-mod:
 *   · на заголовке раздела (.section-title) — весь раздел: заголовок и всё,
 *     что стоит за ним до следующего заголовка того же уровня;
 *   · на карточке или кнопке — только она;
 *   · на самой вкладке (.tab-panel) — вкладка целиком.
 * Раздел, в котором после этого не осталось ни одной видимой кнопки, тоже
 * прячется: пустой заголовок — та же бутафория. Вкладка, где не осталось ни
 * одного рабочего раздела, прячется вместе с кнопкой вкладки (справа и в
 * ящике «Панели» на телефоне). Служебные разделы (data-mod-aux: «Управление
 * ИИ», «Настройки», «Статус GPU») вкладку не держат: вкладка ИИ, где остались
 * только настройки, на УЗИ не нужна.
 *
 * ЧЕМ ПРЯЧЕТСЯ — классом .mod-hidden (display:none !important), а не
 * style.display: у многих рядов своё display, которое ведут их хозяева
 * (строка «Глубина» отсечения, ход разметки органов). Снял класс — и ряд
 * снова такой, каким его оставил хозяин.
 *
 * НЕ ГАСИМ (disabled) — только прячем. Голос, палитра команд и меню «Ещё»
 * жмут кнопки программно; спрятанная кнопка, нажатая так, сама скажет, почему
 * на этой серии не работает. А disabled уже занят: им клинический модуль
 * держит кнопки, пока грузится.
 *
 * Активная вкладка, оказавшаяся спрятанной, уступает место первой видимой —
 * иначе на экране осталось бы содержимое вкладки без её кнопки.
 *
 * EXPORTS: отметитьПанельПоМодальности, панельБезИсследования,
 *          применитьГруппуПанели, текущаяГруппаПанели
 */

import { группаМодальности, видимоДля } from '../core/modalityPanelCore.js';
import { log } from '../utils/logger.js';

const КЛАСС = 'mod-hidden';
const УПРАВЛЕНИЕ = 'button, input, select, textarea';

let _группа = null;

/** Вкладки правой панели: id панели ↔ её кнопки (справа и в мобильном ящике). */
function _вкладки() {
  return Array.from(document.querySelectorAll('.right-tab[data-tab]'), (кн) => ({
    id: кн.dataset.tab,
    панель: document.getElementById(кн.dataset.tab),
    кнопка: кн,
    мобильная: document.getElementById('mpanel-tab-' + кн.dataset.tab.replace(/^tab-/, '')),
  })).filter((в) => в.панель);
}

/** Где искать разделы: каждая вкладка (где бы она ни стояла) и ящик пресетов телефона. */
function _корни() {
  const к = _вкладки().map((в) => в.панель);
  const ящик = document.querySelector('#mobile-presets-drawer .drawer-content');
  if (ящик) к.push(ящик);
  return к;
}

const _спрятан = (эл) => !!эл?.closest?.('.' + КЛАСС);

/** Заголовок и всё за ним до следующего заголовка того же уровня. */
function _раздел(заголовок) {
  const узлы = [];
  for (let эл = заголовок.nextElementSibling; эл && !эл.classList.contains('section-title'); эл = эл.nextElementSibling) узлы.push(эл);
  return узлы;
}

function _разметитьКорень(корень, группа) {
  // 1. С чистого листа: решение каждый раз принимается заново целиком.
  for (const эл of корень.querySelectorAll('.' + КЛАСС)) эл.classList.remove(КЛАСС);

  // 2. Кнопки и карточки со своей меткой.
  for (const эл of корень.querySelectorAll('[data-mod]:not(.section-title)')) {
    if (!видимоДля(эл.dataset.mod, группа)) эл.classList.add(КЛАСС);
  }

  // 3. Разделы. С конца документа к началу: вложенный раздел (кинорендер →
  //    «Тонкая настройка») решается раньше внешнего, и внешний уже видит,
  //    что внутри него спрятано.
  const заголовки = Array.from(корень.querySelectorAll('.section-title')).reverse();
  for (const з of заголовки) {
    const узлы = _раздел(з);
    let прятать = з.dataset.mod != null && !видимоДля(з.dataset.mod, группа);
    if (!прятать) {
      const упр = [];
      for (const у of узлы) {
        if (у.matches(УПРАВЛЕНИЕ)) упр.push(у);
        упр.push(...у.querySelectorAll(УПРАВЛЕНИЕ));
      }
      // Раздел с кнопками, где не осталось ни одной видимой, — пустой
      // заголовок. Раздел без кнопок вовсе (пояснение) не трогаем.
      прятать = упр.length > 0 && упр.every(_спрятан);
    }
    if (прятать) {
      з.classList.add(КЛАСС);
      for (const у of узлы) у.classList.add(КЛАСС);
    }
  }
}

/** Есть ли во вкладке хоть один рабочий раздел. */
function _вкладкаЖива(панель, группа) {
  if (панель.dataset.mod != null && !видимоДля(панель.dataset.mod, группа)) return false;
  const рабочие = Array.from(панель.querySelectorAll('.section-title:not([data-mod-aux])'));
  if (!рабочие.length) return true;     // «Качество», RT: разделы им строит модуль
  return рабочие.some((з) => !_спрятан(з));
}

/**
 * Разметить панель для группы модальности.
 * @param {string|null} группа — из группаМодальности; null — исследования нет
 * @returns {{группа:string|null, спрятаноВкладок:string[], спрятаноЭлементов:number}}
 */
export function применитьГруппуПанели(группа) {
  _группа = группа;
  for (const к of _корни()) _разметитьКорень(к, группа);

  const вкладки = _вкладки();
  const спрятаны = [];
  for (const в of вкладки) {
    const жива = _вкладкаЖива(в.панель, группа);
    в.кнопка.classList.toggle(КЛАСС, !жива);
    в.мобильная?.classList.toggle(КЛАСС, !жива);
    if (!жива) спрятаны.push(в.id);
  }

  // Активная вкладка не может остаться без кнопки.
  const ящик = document.getElementById('mpanel-body');
  const перенесена = ящик?._movedPanel && ящик._movedPanel.parentElement === ящик ? ящик._movedPanel : null;
  if (перенесена) {
    // Телефон: вкладка стоит в ящике «Панели».
    if (спрятаны.includes(перенесена.id)) {
      const первая = вкладки.find((в) => в.мобильная && !спрятаны.includes(в.id));
      if (первая && typeof window.switchMPanel === 'function') window.switchMPanel(первая.id.replace(/^tab-/, ''));
    }
  } else {
    const активная = document.querySelector('.right-tab.active[data-tab]');
    if (активная && спрятаны.includes(активная.dataset.tab)) {
      вкладки.find((в) => !спрятаны.includes(в.id))?.кнопка.click();
    }
  }

  let спрятано = 0;
  for (const к of _корни()) спрятано += к.querySelectorAll('.' + КЛАСС).length;
  log.info(`[Панель по модальности] ${группа ?? 'исследования нет'}: спрятано вкладок ${спрятаны.length}`
    + `${спрятаны.length ? ' (' + спрятаны.join(', ') + ')' : ''}, элементов ${спрятано}`);
  return { группа, спрятаноВкладок: спрятаны, спрятаноЭлементов: спрятано };
}

/**
 * Серия открыта: оставить в панели только то, что ей предназначено.
 * @param {string|null|undefined} dicomМодальность — «CT», «MR»…; пусто — тега нет
 *   (НЕ «серии нет»: тогда показываем всё)
 */
export function отметитьПанельПоМодальности(dicomМодальность) {
  return применитьГруппуПанели(группаМодальности(dicomМодальность));
}

/** Исследования нет (запуск, закрытие): видно всё — смотреть, что умеет программа. */
export function панельБезИсследования() {
  return применитьГруппуПанели(null);
}

/** Для проверок и журнала. */
export function текущаяГруппаПанели() { return _группа; }
